import FileAccessList from "admin/components/FileAccessList";
import SchemaAccessList from "admin/components/SchemaAccessList";
import AccessResponse from "admin/types/interfaces/access";
import { format } from "date-fns";
import { FileText } from "lucide-react";
import { Label } from "~/components/ui/label";

const AccessPanel = ({
  accessData,
  isLoading,
  error,
  context = "user",
}: {
  accessData?: AccessResponse;
  isLoading: boolean;
  error: boolean;
  context?: "user" | "group";
}) => {
  const files = accessData?.access.filter(
    ({ component }) =>
      component.component_type === "glob" && !component.definition.includes("__dac_placeholder")
  );
  const tables = accessData?.access.filter(({ component, tagged_component }) => {
    if (component.component_type === "tag") {
      return tagged_component;
    }
    return component.component_type === "table";
  });

  const showEmpty = !isLoading && !files?.length && !tables?.length && !error;

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 10 }).map((_, idx) => (
          <div key={idx} className="h-4 bg-muted animate-pulse rounded" />
        ))}
      </div>
    );
  }

  if (error && !accessData) {
    return (
      <div className="p-2 border border-destructive/20 bg-destructive/10 text-destructive rounded-md text-sm">
        Unable to load access information
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {showEmpty ? (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <FileText className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">No Access Permissions</h3>
          <p className="text-sm text-muted-foreground">
            This {context} doesn&apos;t have access to any files or database tables yet.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {!!files?.length && (
            <div className="space-y-1">
              <Label>Files</Label>
              <FileAccessList data={files} />
            </div>
          )}
          {!!tables?.length && (
            <div className="space-y-1">
              <Label>Tables</Label>
              <SchemaAccessList data={tables} />
            </div>
          )}
        </div>
      )}
      {accessData?.policies_last_pushed && (
        <p className="text-sm text-muted-foreground">
          Last updated: {format(new Date(accessData.policies_last_pushed), "PPp")}
        </p>
      )}
    </div>
  );
};

export default AccessPanel;
